import { useEffect, useRef, useState } from "react";
import { Send, Scale, Briefcase, Users, Scale as ScaleIcon, Home, Building, Receipt, Landmark, ShoppingCart } from "lucide-react";
import Sidebar from "../components/Sidebar";
import VoiceMicButton from "../components/VoiceMicButton";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import api from "../lib/api";
import { toast } from "sonner";

const ICON_MAP = {
  briefcase: Briefcase,
  users: Users,
  scale: ScaleIcon,
  home: Home,
  building: Building,
  receipt: Receipt,
  landmark: Landmark,
  "shopping-cart": ShoppingCart,
};

export default function Dashboard() {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadConversations = async () => {
    try {
      const res = await api.get("/chat/conversations");
      setConversations(res.data);
    } catch {}
  };

  useEffect(() => {
    loadConversations();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const loadConversation = async (id) => {
    try {
      const res = await api.get(`/chat/conversations/${id}`);
      setActiveConvId(id);
      setMessages(res.data.messages);
      setCategory(res.data.conversation.category || "");
    } catch {
      toast.error("Impossible de charger la conversation");
    }
  };

  const handleNewChat = () => {
    setActiveConvId(null);
    setMessages([]);
    setCategory("");
    setInput("");
  };

  const handleDeleteConv = async (id) => {
    try {
      await api.delete(`/chat/conversations/${id}`);
      toast.success("Consultation supprimée");
      if (activeConvId === id) handleNewChat();
      loadConversations();
    } catch {
      toast.error("Suppression échouée");
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    const tempUserMsg = {
      message_id: `tmp_${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUserMsg]);
    setInput("");
    setSending(true);
    try {
      const res = await api.post("/chat/message", {
        message: text,
        conversation_id: activeConvId,
        category: category || null,
      });
      setActiveConvId(res.data.conversation_id);
      setMessages((m) => [
        ...m.filter((x) => x.message_id !== tempUserMsg.message_id),
        res.data.user_message,
        res.data.ai_message,
      ]);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur serveur");
      setMessages((m) => m.filter((x) => x.message_id !== tempUserMsg.message_id));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleCategoryQuick = (catId) => {
    setCategory(catId);
    const cat = categories.find((c) => c.id === catId);
    if (cat && messages.length === 0) {
      setInput(`J'ai une question en ${cat.label.toLowerCase()}. `);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar
        conversations={conversations}
        onNewChat={handleNewChat}
        onSelectConv={loadConversation}
        activeConvId={activeConvId}
        onDeleteConv={handleDeleteConv}
      />

      <main className="flex-1 flex flex-col min-w-0" data-testid="chat-main">
        {/* Header */}
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Consultation</p>
              <h1 className="font-serif-display text-xl font-medium tracking-tight text-slate-900">
                {activeConvId ? conversations.find((c) => c.conversation_id === activeConvId)?.title || "Consultation" : "Nouvelle consultation"}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-56 rounded-sm" data-testid="category-select">
                  <SelectValue placeholder="Toutes les matières" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`category-option-${c.id}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </header>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto custom-scroll">
          {messages.length === 0 ? (
            <EmptyState
              categories={categories}
              onPickCategory={handleCategoryQuick}
              onQuickQuestion={(q) => setInput(q)}
            />
          ) : (
            <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
              {messages.map((m) => (
                <MessageBubble key={m.message_id} message={m} />
              ))}
              {sending && (
                <div className="flex items-start gap-3 animate-fade-in-up" data-testid="typing-indicator">
                  <div className="h-8 w-8 rounded-sm bg-slate-900 flex items-center justify-center flex-shrink-0">
                    <Scale className="h-4 w-4 text-[#B89C65]" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 shadow-sm">
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-slate-200 bg-white/80 backdrop-blur-xl sticky bottom-0">
          <form onSubmit={handleSend} className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-end gap-2 sm:gap-3">
              <div className="relative flex-1">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Posez votre question juridique... (ou dictez avec le micro)"
                  className="resize-none rounded-sm min-h-[52px] max-h-[200px] border-slate-300 focus-visible:ring-slate-900 pr-12"
                  rows={1}
                  data-testid="chat-input"
                  disabled={sending}
                />
                <div className="absolute top-2 right-2">
                  <VoiceMicButton currentValue={input} onTranscript={setInput} testid="chat-voice-mic" />
                </div>
              </div>
              <Button
                type="submit"
                disabled={!input.trim() || sending}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 h-[52px] w-[52px] p-0 flex-shrink-0"
                data-testid="send-message-button"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center">
              Outil informatif · Ne remplace pas une consultation avec un avocat
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div
      className={`flex items-start gap-3 animate-fade-in-up ${isUser ? "flex-row-reverse" : ""}`}
      data-testid={`message-${message.role}`}
    >
      {!isUser && (
        <div className="h-8 w-8 rounded-sm bg-slate-900 flex items-center justify-center flex-shrink-0">
          <Scale className="h-4 w-4 text-[#B89C65]" />
        </div>
      )}
      <div
        className={`max-w-[78%] px-4 py-3 rounded-sm ${
          isUser
            ? "bg-slate-100 text-slate-900"
            : "bg-white border border-slate-200 text-slate-900 shadow-sm"
        }`}
      >
        <div className="chat-content text-sm whitespace-pre-wrap leading-relaxed">
          {message.content}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ categories, onPickCategory, onQuickQuestion }) {
  const quickQuestions = [
    "Quels sont mes droits en cas de licenciement en Belgique ?",
    "Comment résilier un bail d'habitation en Wallonie ?",
    "Quelle est la procédure de divorce par consentement mutuel ?",
    "Comment contester une amende pour excès de vitesse ?",
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="empty-state">
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-sm bg-slate-900 mb-6">
          <Scale className="h-6 w-6 text-[#B89C65]" />
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3">Droit belge</p>
        <h2 className="font-serif-display text-4xl font-medium tracking-tight text-slate-900 mb-4">
          Comment puis-je vous aider ?
        </h2>
        <p className="text-slate-500 max-w-xl mx-auto">
          Posez votre question ou choisissez une matière pour commencer.
        </p>
      </div>

      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Matières</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {categories.map((c) => {
            const Icon = ICON_MAP[c.icon] || ScaleIcon;
            return (
              <button
                key={c.id}
                onClick={() => onPickCategory(c.id)}
                className="group bg-white border border-slate-200 rounded-sm p-4 text-left hover:border-slate-900 hover:shadow-md transition-all duration-200"
                data-testid={`quick-category-${c.id}`}
              >
                <Icon className="h-4 w-4 text-slate-500 group-hover:text-[#B89C65] transition-colors mb-3" />
                <p className="text-sm font-medium text-slate-900">{c.label}</p>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Exemples de questions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {quickQuestions.map((q, i) => (
            <button
              key={i}
              onClick={() => onQuickQuestion(q)}
              className="text-left text-sm bg-white border border-slate-200 rounded-sm p-4 hover:border-slate-900 hover:shadow-md transition-all duration-200 text-slate-700"
              data-testid={`quick-question-${i}`}
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
